import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageConstants, SupportTypes } from '@uzurv/rideWss/app/constants';
import { SignupErrorConstants } from '@uzurv/rideWss/app/constants/signup-error-constants';
import { ApplicationException } from '@uzurv/rideWss/app/exceptions';
import { EmailVerificationCredentials, EmailVerificationType, LoginCredentials } from '@uzurv/rideWss/app/models';
import { LoadingDialogService, ModalService, RouteParamsService } from '@uzurv/rideWss/app/services';
import {
  AccountLockedException,
  AccountNotVerifiedException,
  AuthDataService,
  AuthenticationException,
  AuthenticationService,
  BadCredentialsException,
  UserNotFoundException
} from '@uzurv/rideWss/auth';
import { AuthenticationStatus } from '@uzurv/rideWss/auth/constants';
import { PasswordService, RiderService } from '@uzurv/rideWss/clara';
import { catchError, filter, finalize, firstValueFrom, from, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as RiderAuthenticationActions from './rider-authentication.action';
import { AuthenticationState } from './rider-authentication.selector';
import { RiderAuthenticationState } from './rider-authentication.state';

@Injectable()
export class RiderAuthenticationEffects {
  clearUserData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.clearUserData),
      map(() => {
        this.authDataService.clearAuthData();
        return RiderAuthenticationActions.logoutSuccess();
      })
    );
  });

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.logout),
      switchMap(_ => {
        return of(RiderAuthenticationActions.clearUserData());
      })
    );
  });

  registerRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.registerRider),
      withLatestFrom(this.store$.select(AuthenticationState)),
      tap(() => this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.signingUp'))),
      switchMap(([action, _]) => {
        return this.riderService.registerRider(action.registrationData).pipe(
          map(user => {
            void this.loadingDialogService.dismissLoadingDialog();

            const riderRegistrationData = Object.assign(
              Object.create(Object.getPrototypeOf(action.registrationData)),
              action.registrationData,
              {
                user: Object.assign(Object.create(Object.getPrototypeOf(action.registrationData.user)), action.registrationData.user, {
                  person: user.person
                })
              }
            );

            return RiderAuthenticationActions.registerRiderSuccess({ registrationData: riderRegistrationData });
          }),
          catchError(error => {
            console.log('error', error);
            void this.loadingDialogService.dismissLoadingDialog();
            return of(RiderAuthenticationActions.registerRiderFailure({ error }));
          })
        );
      })
    );
  });

  registerRiderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.registerRiderSuccess),
      map(action => {
        if (action.registrationData.user.person.emailVerified) {
          return RiderAuthenticationActions.authenticateRider({
            loginCredentials: {
              username: action.registrationData.user.person.emailAddress,
              password: action.registrationData.user.password
            }
          });
        } else {
          return RiderAuthenticationActions.authenticateRiderFailure({
            error: new AccountNotVerifiedException(),
            loginCredentials: {
              username: action.registrationData.user.person.emailAddress,
              password: action.registrationData.user.password
            }
          });
        }
      })
    );
  });

  authenticateRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.authenticateRider),
      switchMap(action => {
        return from(this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.signingIn'))).pipe(
          switchMap(() => {
            return from(
              firstValueFrom(this.authenticationService.login(action.loginCredentials.username, action.loginCredentials.password))
            ).pipe(
              switchMap(authData => [
                RiderAuthenticationActions.authenticateRiderSuccess({
                  loginCredentials: action.loginCredentials,
                  authData: authData
                }),
                RiderAuthenticationActions.refreshAuthenticatedRider({ isFromAuthentication: true })
              ])
            );
          }),
          catchError(error => {
            return of(RiderAuthenticationActions.authenticateRiderFailure({ error: error, loginCredentials: action.loginCredentials }));
          }),
          finalize(() => {
            void this.loadingDialogService.dismissLoadingDialog();
          })
        );
      })
    );
  });

  resendVerificationEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.resendVerificationEmail),
      tap(() => this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.resendingVerificationEmail'))),
      switchMap(action => {
        return from(this.riderService.resendVerifyEmail(action.emailAddress)).pipe(
          map(() => {
            void this.loadingDialogService.dismissLoadingDialog();
            return RiderAuthenticationActions.resendVerificationEmailSuccess({ emailAddress: action.emailAddress });
          }),
          catchError(error => {
            void this.loadingDialogService.dismissLoadingDialog();
            return of(RiderAuthenticationActions.resendVerificationEmailFailure({ error }));
          })
        );
      })
    );
  });

  resendVerificationEmailSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.resendVerificationEmailSuccess),
        map(action => {
          from(
            this.modalService.presentAlertDialog({
              htmlContent: this.domSanitizer.bypassSecurityTrustHtml(
                this.translateService.instant('confirmEmail.resendSuccess', {
                  email: action.emailAddress
                })
              )
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  resendVerificationEmailFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.resendVerificationEmailFailure),
        withLatestFrom(this.store$.select(AuthenticationState)),
        switchMap(([action, state]) => {
          if (SignupErrorConstants.RIDER_COMPLETED_VERIFICATION.test(action.error.message)) {
            return from(
              this.modalService.presentAlertDialog({
                message: this.translateService.instant('registration.error.riderCompletedVerification'),
                buttons: [
                  {
                    text: this.translateService.instant('button.ok'),
                    role: 'confirm',
                    handler: () => {
                      this.store$.dispatch(
                        RiderAuthenticationActions.authenticateRider({
                          loginCredentials: {
                            username: state.emailVerificationCredentials.username,
                            password: state.emailVerificationCredentials.password
                          }
                        })
                      );
                    }
                  }
                ]
              })
            );
          } else {
            return from(this.modalService.createErrorModalDialog(action.error));
          }
        })
      );
    },
    { dispatch: false }
  );

  forgotPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.forgotPassword),
      tap(() => this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.forgotPassword'))),
      switchMap(action => {
        return from(this.passwordService.forgotPassword(action.emailAddress)).pipe(
          map(() => {
            void this.loadingDialogService.dismissLoadingDialog();
            return RiderAuthenticationActions.forgotPasswordSuccess({ emailAddress: action.emailAddress });
          }),
          catchError(error => {
            void this.loadingDialogService.dismissLoadingDialog();
            return of(RiderAuthenticationActions.forgotPasswordFailure({ error }));
          })
        );
      })
    );
  });

  forgotPasswordFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.forgotPasswordFailure),
        map(action => {
          if (action.error instanceof UserNotFoundException) {
            return from(
              this.modalService.presentAlertDialog({
                title: this.translateService.instant('forgotPassword.error.title.userNotFound'),
                message: this.translateService.instant('forgotPassword.error.message.userNotFound')
              })
            );
          } else {
            return from(this.modalService.createErrorModalDialog(action.error));
          }
        })
      );
    },
    { dispatch: false }
  );

  refreshAuthenticatedRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.refreshAuthenticatedRider),
      switchMap(action => {
        if (!this.authDataService.isLoggedIn) {
          const error: AuthenticationException = new AuthenticationException();
          return of(
            RiderAuthenticationActions.refreshAuthenticatedRiderFailure({ error: error, isFromAuthentication: action.isFromAuthentication })
          );
        }
        return this.riderService.findRiderByUsername(this.authDataService.currentUserAuthData.username).pipe(
          switchMap(rider => {
            return of(
              RiderAuthenticationActions.refreshAuthenticatedRiderSuccess({
                rider: rider,
                isFromAuthentication: action.isFromAuthentication
              })
            );
          }),
          catchError(error => {
            return of(
              RiderAuthenticationActions.refreshAuthenticatedRiderFailure({
                error: error,
                isFromAuthentication: action.isFromAuthentication
              })
            );
          })
        );
      })
    );
  });

  refreshAuthenticatedRiderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiderAuthenticationActions.refreshAuthenticatedRiderSuccess),
      filter(action => action.isFromAuthentication),
      map(action => {
        return RiderAuthenticationActions.riderAuthenticationComplete({ rider: action.rider });
      })
    )
  );

  riderAuthenticationComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RiderAuthenticationActions.authenticateRiderSuccess),
        tap(() => {
          if (this.activatedRoute.snapshot.queryParamMap.get('redirectUrl')) {
            this.router.navigateByUrl(this.router.routerState.snapshot.root.queryParams['redirectUrl']);
          } else {
            this.router.navigate(['/rides']);
          }
        })
      ),
    { dispatch: false }
  );

  storeState$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          RiderAuthenticationActions.refreshAuthenticatedRiderSuccess,
          RiderAuthenticationActions.logoutSuccess,
          RiderAuthenticationActions.setCurrentDeviceInstallation
        ),
        withLatestFrom(this.store$.select(AuthenticationState)),
        map(([_, state]) => {
          localStorage.setItem(LocalStorageConstants.RIDER_AUTHENTICATION_STATE_KEY, JSON.stringify(state));
        })
      );
    },
    { dispatch: false }
  );

  rehydrateState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.rehydrateState),
      withLatestFrom(this.store$.select(AuthenticationState)),
      map(([_, state]) => {
        try {
          if (!localStorage.getItem(LocalStorageConstants.RIDER_AUTHENTICATION_STATE_KEY)) {
            // noinspection ExceptionCaughtLocallyJS
            throw new ApplicationException('No State Stored');
          }
          const storedState: RiderAuthenticationState = JSON.parse(
            localStorage.getItem(LocalStorageConstants.RIDER_AUTHENTICATION_STATE_KEY)
          );
          return RiderAuthenticationActions.rehydrateStateSuccess({ state: storedState });
        } catch (err) {
          return RiderAuthenticationActions.rehydrateStateSuccess({ state: state });
        }
      })
    );
  });

  rehydrateStateSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.rehydrateStateSuccess),
        withLatestFrom(this.store$.select(AuthenticationState)),
        map(([_, state]) => {
          if (state.authenticationStatus === AuthenticationStatus.LOGGED_IN) {
            this.store$.dispatch(RiderAuthenticationActions.riderAuthenticationComplete({ rider: state.authenticatedRider }));
          }
        })
      );
    },
    {
      dispatch: false
    }
  );

  fetchRiderAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.fetchRiderAddress, RiderAuthenticationActions.refreshAuthenticatedRiderSuccess),
      withLatestFrom(this.store$.select(AuthenticationState)),
      switchMap(([_, state]) => {
        return this.riderService.findAddressByRiderId(state.authenticatedRider.id).pipe(
          map(address => {
            return RiderAuthenticationActions.fetchRiderAddressSuccess({ address: address });
          }),
          catchError(error => of(RiderAuthenticationActions.fetchRiderAddressFail({ error: error })))
        );
      })
    );
  });

  addRiderAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.addRiderAddress),
      withLatestFrom(this.store$.select(AuthenticationState)),
      switchMap(([action, _]) => {
        void this.loadingDialogService.presentLoadingDialog('dialog.saving');
        return this.riderService.addAddress(action.personId, action.address).pipe(
          map(address => {
            void this.loadingDialogService.dismissLoadingDialog();
            void this.displayAddressUpdatedAlert();
            return RiderAuthenticationActions.addRiderAddressSuccess({ address: address });
          }),
          catchError(error => of(RiderAuthenticationActions.addRiderAddressFail({ error: error })))
        );
      })
    );
  });

  refreshAuthenticatedRiderFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.refreshAuthenticatedRiderFailure),
      filter(action => action.isFromAuthentication),
      map(_ => RiderAuthenticationActions.logout())
    );
  });

  handleRegisterRiderErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.registerRiderFailure),
        switchMap(action => {
          if (SignupErrorConstants.EMAIL_ALREADY_IN_USE.test(action.error.message)) {
            return from(
              this.modalService.presentAlertDialog({
                title: this.translateService.instant('registration.message.emailInUse'),
                message: this.translateService.instant('registration.message.loginInstead'),
                buttons: [
                  {
                    text: this.translateService.instant('button.dismiss'),
                    role: 'cancel'
                  },
                  {
                    text: this.translateService.instant('button.login'),
                    role: 'default',
                    handler: () => {
                      void this.router.navigate(['/login']);
                    }
                  }
                ]
              })
            );
          } else if (SignupErrorConstants.USER_ALREADY_EXISTS.test(action.error.message)) {
            return from(
              this.modalService.presentAlertDialog({
                title: this.translateService.instant('registration.message.userAlreadyExists'),
                message: this.translateService.instant('registration.message.loginInstead'),
                buttons: [
                  {
                    text: this.translateService.instant('button.dismiss'),
                    role: 'cancel'
                  },
                  {
                    text: this.translateService.instant('button.login'),
                    role: 'default',
                    handler: () => {
                      void this.router.navigate(['/login']);
                    }
                  }
                ]
              })
            );
          } else if (SignupErrorConstants.RIDER_APP_NOT_AVAILABLE_FOR_ORGANIZATION.test(action.error.message)) {
            return from(
              this.modalService.presentAlertDialog({
                message: this.translateService.instant('registration.error.riderAppNotAvailableForOrganizations')
              })
            );
          } else if (SignupErrorConstants.WEAK_PASSWORD.test(action.error.message)) {
            return from(
              this.modalService.presentAlertDialog({
                message: this.translateService.instant('signup.error.weakPassword')
              })
            );
          } else if (
            SignupErrorConstants.RIDER_NOT_FOUND.test(action.error.message) ||
            SignupErrorConstants.FAILED_PROOFING.test(action.error.message)
          ) {
            this.routeParamsService.setRouteParams('/call-uzurv-support', {
              supportType: SupportTypes.PROOFING_SIGNUP_ERROR
            });
            void this.router.navigate(['/call-uzurv-support']);
          } else if (SignupErrorConstants.RIDER_PREVIOUSLY_REGISTERED.test(action.error.message)) {
            return from(
              this.modalService.presentAlertDialog({
                message: this.translateService.instant('registration.error.riderPreviouslyRegistered')
              })
            );
          } else {
            return from(this.modalService.createErrorModalDialog(action.error));
          }
        })
      );
    },
    { dispatch: false }
  );

  updateRiderPhoneNumber$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.updateRiderPhoneNumber),
      switchMap(action => {
        return from(this.loadingDialogService.presentLoadingDialog(action.dialogMessage)).pipe(
          switchMap(() =>
            this.riderService.patchRider(action.rider).pipe(
              map(rider => {
                void this.loadingDialogService.dismissLoadingDialog();
                void this.displayPhoneNumberUpdatedSuccessAlert();
                return RiderAuthenticationActions.updateRiderPhoneNumberSuccess({ rider });
              }),
              catchError(error => {
                void this.loadingDialogService.dismissLoadingDialog();
                return of(RiderAuthenticationActions.updateRiderPhoneNumberFailure({ error }));
              })
            )
          )
        );
      })
    );
  });

  updateRiderName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.updateRiderName),
      switchMap(action => {
        return from(this.loadingDialogService.presentLoadingDialog(action.dialogMessage)).pipe(
          switchMap(() =>
            this.riderService.patchRider(action.rider).pipe(
              map(rider => {
                void this.loadingDialogService.dismissLoadingDialog();
                return RiderAuthenticationActions.updateRiderNameSuccess({ rider });
              }),
              catchError(error => {
                void this.loadingDialogService.dismissLoadingDialog();
                return of(RiderAuthenticationActions.updateRiderNameFailure({ error }));
              })
            )
          )
        );
      })
    );
  });

  fetchRiderMobilityProfileDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RiderAuthenticationActions.refreshAuthenticatedRiderSuccess,
        RiderAuthenticationActions.updateRiderMobilityProfileDetailSuccess,
        RiderAuthenticationActions.verifyRiderMobilityProfileDetailSuccess
      ),
      withLatestFrom(this.store$.select(AuthenticationState)),
      switchMap(([_, state]) => {
        return this.riderService.getRiderMobilityProfileDetail(state.authenticatedRider.id).pipe(
          map(mobilityProfileDetail => RiderAuthenticationActions.fetchRiderMobilityProfileDetailSuccess({ mobilityProfileDetail })),
          catchError(error => of(RiderAuthenticationActions.fetchRiderMobilityProfileDetailFailure({ error })))
        );
      })
    );
  });

  updateRiderMobilityProfileDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.updateRiderMobilityProfileDetail),
      withLatestFrom(this.store$.select(AuthenticationState)),
      switchMap(([action, state]) => {
        return this.riderService.updateRiderMobilityProfileDetail(state.authenticatedRider.id, action.mobilityProfileDetail).pipe(
          map(mobilityProfileDetail => {
            return RiderAuthenticationActions.updateRiderMobilityProfileDetailSuccess({ mobilityProfileDetail });
          }),
          catchError(error => of(RiderAuthenticationActions.updateRiderMobilityProfileDetailFailure({ error })))
        );
      })
    );
  });

  verifyRiderMobilityProfileDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.verifyRiderMobilityProfileDetail),
      withLatestFrom(this.store$.select(AuthenticationState)),
      switchMap(([action, state]) => {
        return this.riderService.verifyRiderMobilityProfileDetail(state.authenticatedRider.id, action.mobilityProfileDetail).pipe(
          map(mobilityProfileDetail => {
            return RiderAuthenticationActions.verifyRiderMobilityProfileDetailSuccess({ mobilityProfileDetail });
          }),
          catchError(error => of(RiderAuthenticationActions.verifyRiderMobilityProfileDetailFailure({ error })))
        );
      })
    );
  });

  riderMobilityProfileFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          RiderAuthenticationActions.fetchRiderMobilityProfileDetailFailure,
          RiderAuthenticationActions.updateRiderMobilityProfileDetailFailure,
          RiderAuthenticationActions.verifyRiderMobilityProfileDetailFailure
        ),
        map(action => {
          void this.modalService.createErrorModalDialog(action.error);
        })
      );
    },
    { dispatch: false }
  );

  handleAuthenticationErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.authenticateRiderFailure),
        map(action => {
          if (action.error instanceof AuthenticationException) {
            if (action.error instanceof AccountNotVerifiedException) {
              void this.accountNotVerified(action.loginCredentials);
            } else if (action.error instanceof BadCredentialsException) {
              this.displayBadCredentialsModal();
            } else if (action.error instanceof AccountLockedException) {
              void this.router.navigate(['/forgot-password']);
            } else {
              this.displayPushAuthenticationFailedErrorPage(action.error);
            }
          } else {
            void this.modalService.presentAlertDialog({
              title: this.translateService.instant('error.dialog.title.loginFailed'),
              message: action.error.message
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  checkEmailVerification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiderAuthenticationActions.checkEmailVerification),
      withLatestFrom(this.store$.select(AuthenticationState)),
      map(([_, state]) => {
        if (state.emailVerificationCredentials?.emailVerificationType === EmailVerificationType.UPDATE) {
          void this.displayEmailUpdatedAlert();
        } else if (state.emailVerificationCredentials?.emailVerificationType === EmailVerificationType.SIGNUP) {
          void this.displaySignupEmailVerifiedAlert();
        }
        return RiderAuthenticationActions.checkEmailVerificationSuccess();
      })
    );
  });

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.addRiderAddressFail, RiderAuthenticationActions.fetchRiderAddressFail),
        map(action => {
          void this.loadingDialogService.dismissLoadingDialog();
          void this.modalService.createNgrxErrorModalDialog(action.error.message);
        })
      );
    },
    { dispatch: false }
  );

  dismissLoadingDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.logoutSuccess),
        map(_ => {
          void this.loadingDialogService.dismissLoadingDialog();
        })
      );
    },
    { dispatch: false }
  );

  logoutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.logoutSuccess),
        map(_ => {
          if (!this.isPublicRoute(this.router.url)) {
            void this.router.navigate(['/login']);
          }
        })
      );
    },
    { dispatch: false }
  );

  handlePatchRiderInfoErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RiderAuthenticationActions.updateRiderPhoneNumberFailure, RiderAuthenticationActions.updateRiderNameFailure),
        map(action => {
          void this.modalService.createErrorModalDialog(action.error);
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private authDataService: AuthDataService,
    private authenticationService: AuthenticationService,
    private domSanitizer: DomSanitizer,
    private loadingDialogService: LoadingDialogService,
    private store$: Store<State<RiderAuthenticationState>>,
    private modalService: ModalService,
    private passwordService: PasswordService,
    private riderService: RiderService,
    private router: Router,
    private routeParamsService: RouteParamsService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute
  ) {}

  private async accountNotVerified(loginCredentials: LoginCredentials) {
    this.store$.dispatch(
      RiderAuthenticationActions.setEmailVerificationCredentials({
        emailVerificationCredentials: new EmailVerificationCredentials(loginCredentials.username, loginCredentials.password)
      })
    );

    from(this.router.navigate(['/registration/registration-verify-email']));
    if (this.router.url === '/registration/registration-verify-email') {
      from(
        this.modalService.presentAlertDialog({
          htmlContent: this.domSanitizer.bypassSecurityTrustHtml(
            this.translateService.instant('emailVerification.notVerified', {
              email: loginCredentials.username
            })
          ),
          buttons: [
            {
              text: this.translateService.instant('button.gotIt'),
              role: 'confirm'
            }
          ]
        })
      );
    }
  }

  private displayBadCredentialsModal() {
    if (this.router.url === '/registration/registration-verify-email') {
      void this.router.navigate(['/login']);
    }

    return from(
      this.modalService.presentAlertDialog({
        message: this.translateService.instant('login.error.badCredentials')
      })
    );
  }

  private displayPushAuthenticationFailedErrorPage(error: Error) {
    this.routeParamsService.setRouteParams('/authentication-failed-error', {
      authenticationException: error
    });
    void this.router.navigate(['/authentication-failed-error']);
  }

  async displaySignupEmailVerifiedAlert() {
    await this.modalService.presentAlertDialog({
      icon: '../assets/ride-wss-app/svgs/icon_verified.svg',
      alignTextCenter: true,
      message: this.translateService.instant('emailVerification.verified'),
      buttons: [
        {
          text: this.translateService.instant('button.getStarted'),
          role: 'default'
        }
      ]
    });
  }

  async displayEmailUpdatedAlert() {
    await this.modalService.presentAlertDialog({
      icon: 'uz-check',
      iconColor: 'success',
      alignTextCenter: true,
      title: this.translateService.instant('editEmail.alert.title.success'),
      titleColor: 'success',
      message: this.translateService.instant('editEmail.alert.message.success'),
      buttons: [
        {
          text: this.translateService.instant('button.done'),
          color: 'primary',
          role: 'default'
        }
      ]
    });
  }

  async displayAddressUpdatedAlert() {
    await this.modalService.presentAlertDialog({
      icon: 'uz-check',
      iconColor: 'success',
      alignTextCenter: true,
      title: this.translateService.instant('alert.title.success'),
      titleColor: 'success',
      message: this.translateService.instant('editAddress.alert.message.success'),
      buttons: [
        {
          role: 'default',
          text: this.translateService.instant('button.done'),
          color: 'primary',
          handler: () => {}
        }
      ]
    });
  }

  async displayPhoneNumberUpdatedSuccessAlert() {
    await this.modalService.presentAlertDialog({
      icon: 'uz-check',
      iconColor: 'success',
      alignTextCenter: true,
      title: this.translateService.instant('editPhoneNumber.alert.success.title'),
      message: this.translateService.instant('editPhoneNumber.alert.success.message'),
      buttons: [
        {
          text: this.translateService.instant('button.done'),
          role: 'default',
          color: 'primary'
        }
      ]
    });
  }

  private isPublicRoute(url: string): boolean {
    const normalizedRouterPath = (url ?? '').split('?')[0].split('#')[0].replace(/\/+$/, '');
    const browserPath = window.location.pathname.split('?')[0].split('#')[0].replace(/\/+$/, '');
    const currentPath = normalizedRouterPath && normalizedRouterPath !== '/' ? normalizedRouterPath : browserPath;

    const firstSegment = currentPath.replace(/^\/+/, '').split('/')[0] ?? '';

    return currentPath.endsWith('/login') || currentPath.endsWith('/forgot-password') || firstSegment.startsWith('registration');
  }
}
